import React, { useState } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  MessageCircle, 
  Clock, 
  Target,
  Book,
  Users,
  MapPin,
  Building
} from 'lucide-react';
import BookingPopup from './BookingPopup';

const FAQItem = ({ question, answer, isOpen, onClick, icon: Icon }) => {
  return (
    <div className="border-b border-teal-400/30 last:border-0">
      <button
        className={`w-full py-6 flex justify-between items-center text-left transition-colors duration-300 ${
          isOpen ? 'bg-teal-700/30' : 'hover:bg-teal-700/10'
        }`}
        onClick={onClick}
      >
        <div className="flex items-center space-x-4 pr-8">
          <div className="flex-shrink-0 w-10 flex justify-center">
            <Icon className="h-6 w-6 text-yellow-300" />
          </div>
          <h3 className="text-lg font-semibold text-white">{question}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-yellow-300 flex-shrink-0 mr-4 transition-transform duration-300" />
        ) : (
          <ChevronDown className="h-5 w-5 text-yellow-300 flex-shrink-0 mr-4 transition-transform duration-300" />
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <p className="text-white/90 px-20 py-6 italic leading-relaxed bg-teal-700/20">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      icon: HelpCircle,
      question: "Why is career counselling essential in today's world?",
      answer: "In today's rapidly evolving job market, career counselling has become crucial. With over 3000+ career options available, making an informed decision can be overwhelming. Professional career counselling helps you understand your strengths, interests, and personality traits, matching them with suitable career paths. It prevents costly career mistakes, saves time, and ensures you choose a path that aligns with both your capabilities and market demands."
    },
    {
      icon: MapPin, // Add this import: import { MapPin } from 'lucide-react';
      question: "Do you provide career counselling services in Jamshedpur and Ranchi?",
      answer: "Yes! We provide comprehensive career counselling services for students and professionals in Jamshedpur, Ranchi, and throughout Jharkhand. Our sessions are available both online and through personalized consultations, ensuring you receive expert guidance regardless of your location. We understand the unique career landscape of Jharkhand and provide tailored advice for local opportunities."
    },
    {
      icon: Users,
      question: "How do online sessions work for clients in Jharkhand?",
      answer: "Our online career counselling sessions are designed to be as effective as in-person meetings. We use high-definition video conferencing for face-to-face interaction, share digital assessment reports in real-time, and provide personalized career roadmaps. Clients from Jamshedpur and Ranchi receive the same high-quality service with the convenience of attending from home. Technical support is available to ensure smooth sessions."
    },
    {
      icon: Target,
      question: "How does career counselling help in making better decisions?",
      answer: "Career counselling provides structured guidance through scientific psychometric assessments, market insights, and expert analysis. Instead of relying on peer pressure or limited information, you get a comprehensive understanding of various career options, their requirements, and growth potential. This data-driven approach helps you make informed decisions based on your actual capabilities rather than assumptions."
    },
    {
      icon: Users,
      question: "Isn't career guidance from parents and teachers enough?",
      answer: "While parents and teachers provide valuable advice, professional career counselling offers unbiased, up-to-date industry knowledge. Career counselors have access to the latest market trends, detailed information about emerging careers, and specialized tools to assess your aptitude. They can provide insights about careers that might not be familiar to parents or teachers, especially in rapidly evolving fields like technology, digital media, or sustainable development."
    },
    {
      icon: Building, // Add this import: import { Building } from 'lucide-react';
      question: "What specific career guidance do you provide for Jamshedpur's industrial sector?",
      answer: "Given Jamshedpur's prominence as a steel and industrial hub, we provide specialized guidance for careers in manufacturing, metallurgy, engineering, and industrial management. Our counselling covers opportunities in major companies like Tata Steel, career progression in technical fields, and emerging opportunities in industrial automation and sustainable manufacturing practices."
    },
    {
      icon: Clock,
      question: "When is the right time to seek career counselling?",
      answer: "Career counselling is beneficial at various life stages: during school when choosing subjects (8th-10th grade), before college admissions, during graduation, or when considering a career switch. Early counselling helps in proper academic planning, while mid-career counselling assists in professional growth and transition. The best time is when you're approaching a major career or educational decision point."
    },
    {
      icon: Book,
      question: "How does your career counselling process work?",
      answer: "Our process begins with a FREE initial consultation to understand your needs. We then conduct comprehensive psychometric assessments to evaluate your interests, aptitudes, and personality traits. Based on these results, we provide detailed career mapping, subject selection guidance, and create a personalized roadmap for your career development."
    },
    {
      icon: Clock,
      question: "How long does a typical counselling session last?",
      answer: "A typical counselling session lasts 60-90 minutes. The first session is FREE, allowing you to understand our process and discuss your career goals without any commitment. Follow-up sessions are scheduled based on your specific needs and the chosen counselling program."
    },
    {
      icon: MessageCircle,
      question: "How do I schedule a counselling session?",
      answer: "You can schedule a session by clicking the 'Book Your Appointment' button on our website, calling us at 9731166822, or emailing us at sudutta@gmail.com. We offer both in-person sessions in Bangalore and online counselling for clients across Jharkhand and Karnataka, including Jamshedpur and Ranchi."
    }
  ];

  return (
    <div className="py-16 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-teal-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-50 rounded-full -ml-32 -mb-32 opacity-50"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about career counselling and our services. <br/>
            Can't find what you're looking for? Feel free to reach out to us directly.
          </p>
        </div>

        {/* FAQ Cards */}
        <div className="bg-teal-600 rounded-2xl shadow-xl overflow-hidden">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              icon={faq.icon}
              isOpen={index === openIndex}
              onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
            />
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12 space-y-6">
          <p className="text-gray-600">Still have questions?</p>
          <button 
            onClick={() => setIsBookingOpen(true)}
            className="inline-flex items-center px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl space-x-2"
          >
            <MessageCircle className="h-5 w-5" />
            <span>Ask Us a Question</span>
          </button>
        </div>
      </div>

      {isBookingOpen && (
        <BookingPopup onClose={() => setIsBookingOpen(false)} />
      )}
    </div>
  );
};

export default FAQSection;